import { Component, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ICoding, IDENTITY_VERIFICATION_METHOD, IHealthcareservice, IIdentityVerificationMethods } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FormsData } from "src/app/helpers/formsData";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";

@Component({
  selector: "app-identity-verif-methods-page",
  templateUrl: "./identity-verif-methods-page.component.html",
  styleUrls: ["./identity-verif-methods-page.component.scss"],
})
export class IdentityVerifMethodsPageComponent implements OnInit {
  public serviceWithVerifMethods: IHealthcareservice;
  public availableLangs: ICoding[];

  private onDestroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private sessionService: SessionService,
    private formsData: FormsData,
    private healthcareService: HealthcareserviceService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.initIdentityVeridMethods();
    });
    this.initIdentityVeridMethods();
  }

  private async initIdentityVeridMethods(): Promise<void> {
    const currentServiceRef = this.sessionService.currentService;
    if (!currentServiceRef) return;
    if (currentServiceRef.reference !== this.sessionService.allsOption) {
      const langs = await this.formsData.getLanguages();
      this.availableLangs = langs;
      const currentService = this.userService.allServices.find((s) => s.serviceRef === currentServiceRef.reference);
      if (currentService && !currentService.identityVerifMethods?.length) {
        currentService.identityVerifMethods = await this.defaultIdentityVerificationMethodList();
      }
      this.serviceWithVerifMethods = currentService;
    }
  }

  public addVerifMethod(): void {
    this.serviceWithVerifMethods.identityVerifMethods.push({
      term: (this.serviceWithVerifMethods.identityVerifMethods?.length + 1).toString(),
      fr: "",
      en: "",
      de: "",
      nl: "",
      it: "",
      pt: "",
      activated: false,
      level: "low",
    });
  }

  public updateVerifMethods(): void {
    this.serviceWithVerifMethods.identityVerifMethods.forEach((verifMethod, index) => {
      if (!verifMethod.fr && !verifMethod.en && !verifMethod.de && !verifMethod.nl && !verifMethod.it) {
        this.serviceWithVerifMethods.identityVerifMethods.splice(index, 1);
      }
    });
    this.healthcareService
      .update(this.serviceWithVerifMethods)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.snackBar.open(this.translateService.instant("common.saveSuccess"), undefined, {
          duration: 3000,
          horizontalPosition: "center",
          verticalPosition: "bottom",
        });
      });
  }

  private async defaultIdentityVerificationMethodList(): Promise<IIdentityVerificationMethods[]> {
    const keys = Object.keys(IDENTITY_VERIFICATION_METHOD).slice(0, Object.keys(IDENTITY_VERIFICATION_METHOD).length / 2);

    const langs = ["fr", "nl", "en", "de", "it", "pt"];

    // Load all translations because ngx-translate was not made to find a single translation in another lang
    await Promise.all(langs.map((lang) => this.translateService.getTranslation(lang).toPromise()));

    // Resolve nested keys in object:
    const resolveKey = (obj: any, path: string): string => {
      return path.split(".").reduce((acc, part) => acc?.[part], obj) ?? undefined;
    };

    const translationMap = langs.reduce((acc, lang) => {
      // getTranslation() returns the cached object after the first call
      acc[lang] = this.translateService.translations[lang];
      return acc;
    }, {} as Record<string, any>);

    return keys.map((key) => {
      const trslKey = `forms.identityVerificationMethod.${IDENTITY_VERIFICATION_METHOD[key]}`;

      return {
        level: "high",
        activated: true,
        term: String(key),
        fr: resolveKey(translationMap["fr"], trslKey),
        nl: resolveKey(translationMap["nl"], trslKey),
        en: resolveKey(translationMap["en"], trslKey),
        de: resolveKey(translationMap["de"], trslKey),
        it: resolveKey(translationMap["it"], trslKey),
        pt: resolveKey(translationMap["pt"], trslKey),
      };
    });
  }
}
